import { useCallback, useState } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import { ButtonProps } from 'ts/common/components/gallery';
import { ContractContext } from 'ts/common/contract/types';
import { needsToSign } from 'ts/common/contract/utils';
import { usePrintOrDownloadContract } from 'ts/common/hooks/contract/usePrintOrDownloadContract';

interface UseContractResponsiveButtonsProps {
    t: (key: string) => string;
    onBack: () => void;
    onSign: () => void;
    context: ContractContext;
    heartbeat?: () => void;
    contract?: SpApi.IBaseContract;
    contractUserEmail?: string;
    isConfirmation?: boolean;
}

export const useContractResponsiveButtons = ({
    t,
    onBack,
    onSign,
    context,
    heartbeat,
    contract,
    contractUserEmail,
    isConfirmation
}: UseContractResponsiveButtonsProps) => {
    const { printContract, downloadContract } = usePrintOrDownloadContract(
        contract?.links?.alternate?.href ?? ''
    );
    const isMobile = useBreakpointValue({ base: true, md: false });
    const [fillableFieldData, setFillableFieldData] = useState<SpApi.IContractFillableField[]>([]);
    const [currentFieldIndex, setCurrentFieldIndex] = useState<number>(-1);
    const [hasStartedFilling, setHasStartedFilling] = useState<boolean>(false);

    const requiresSecondClientSignature =
        contract?.contacts?.length &&
        contract?.contacts?.length > 1 &&
        contract?.signatureClients?.length === 1;
    const allFillableFieldsCompleted = fillableFieldData.every((field) => field.value !== '');

    const handleScrollToField = useCallback((fieldId: string) => {
        const fieldElement = document.getElementById(fieldId);

        if (fieldElement) {
            fieldElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            fieldElement.focus();
        }
    }, []);

    const handleStartFilling = useCallback(() => {
        setHasStartedFilling(true);
        setCurrentFieldIndex(0);
        const firstEmptyField = fillableFieldData.findIndex((field) => field.value === '');

        if (firstEmptyField !== -1) {
            handleScrollToField(fillableFieldData[firstEmptyField].id);
        }
    }, [fillableFieldData, handleScrollToField]);

    const handleNextField = useCallback(() => {
        const nextEmptyField = fillableFieldData.findIndex(
            (field, index) => field.value === '' && index > currentFieldIndex
        );

        if (nextEmptyField !== -1) {
            setCurrentFieldIndex(nextEmptyField);
            handleScrollToField(fillableFieldData[nextEmptyField].id);
        }
    }, [fillableFieldData, currentFieldIndex, handleScrollToField]);

    const handlePreviousField = useCallback(() => {
        if (currentFieldIndex === 0) {
            onBack();
        } else if (currentFieldIndex > 0) {
            const prevIndex = currentFieldIndex - 1;
            setCurrentFieldIndex(prevIndex);
            handleScrollToField(fillableFieldData[prevIndex].id);
        }
    }, [currentFieldIndex, fillableFieldData, handleScrollToField, onBack]);

    const getNextButtonText = useCallback(() => {
        if (!isMobile) {
            return t('signDocument');
        }

        if (requiresSecondClientSignature) {
            return t('signDocument');
        }

        if (hasStartedFilling && allFillableFieldsCompleted) {
            return t('signDocument');
        }

        if (!hasStartedFilling) {
            return t('start');
        }

        return t('next');
    }, [hasStartedFilling, isMobile, allFillableFieldsCompleted, t, requiresSecondClientSignature]);

    const isCurrentFieldFilled = useCallback(() => {
        if (!hasStartedFilling || currentFieldIndex === -1) {
            return true; // Allow clicking Start button
        }

        return fillableFieldData[currentFieldIndex]?.value !== '';
    }, [hasStartedFilling, currentFieldIndex, fillableFieldData]);

    const handleContractStart = useCallback(() => {
        setHasStartedFilling(true);
        setCurrentFieldIndex(0);
    }, []);

    const getButtons = (): ButtonProps[] => {
        const showDownloadAndPrint =
            isConfirmation || !needsToSign(contractUserEmail ?? '', contract, context);

        if (showDownloadAndPrint) {
            return [
                {
                    text: t('download'),
                    onClick: () => {
                        heartbeat?.();
                        downloadContract();
                    },
                    variant: 'outline primary',
                    cursor: 'pointer'
                },
                {
                    text: t('print'),
                    onClick: () => {
                        heartbeat?.();
                        printContract();
                    },
                    variant: 'primary',
                    cursor: 'pointer'
                }
            ];
        }

        return [
            {
                text: t('back'),
                onClick: () => {
                    heartbeat?.();

                    if (isMobile && hasStartedFilling) {
                        handlePreviousField();
                    } else {
                        onBack();
                    }
                },
                variant: 'outline primary',
                cursor: 'pointer'
            },
            {
                text: getNextButtonText(),
                onClick: () => {
                    heartbeat?.();

                    if (isMobile) {
                        if (requiresSecondClientSignature) {
                            onSign();
                        }

                        if (hasStartedFilling && allFillableFieldsCompleted) {
                            onSign();
                        }

                        if (!hasStartedFilling) {
                            handleStartFilling();
                        }

                        handleNextField();
                    } else {
                        onSign();
                    }
                },
                isDisabled:
                    (!allFillableFieldsCompleted && !isMobile) ||
                    (isMobile && !isCurrentFieldFilled()),
                isLoading: false,
                variant: 'primary',
                type: 'submit',
                cursor: 'pointer'
            }
        ];
    };

    return {
        fillableFieldData,
        setFillableFieldData,
        handleContractStart,
        getButtons
    };
};
